import _ from "underscore";
import React from "react";
import { ExternalLink, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Tasks from "../tasks/index";
import EnquiryLearners from "./learners/index";
import EnquiryLessonPlan from "./lesson_plans/show";
import CloseEnquiry from "./close/show";
import ClaimButton from "./buttons/claim";
import Timeago from "../timeago";
import ReactivateButton from "./buttons/reactivate";
import EnquiryHeader from "./header";
import EnquiryOverview from "./overview";
import ActivityStream from "./activity_stream/ActivityStream";
import store from "../../state/store";
import EditCommissionManager from "./commission/edit";
import SalesOrder from "./orders";
import DateComponent from "../date";

class Enquiry extends React.Component {
  onAddLearner = () => {
    store.emit("enquiry:learners:create", this.props.enquiry.id, "");
  };

  renderLessonPlan = (lesson_plan) => {
    if (this.props.ui.enquiry.tab_key.indexOf("lp") === 0) {
      if (lesson_plan.id === parseInt(this.props.ui.enquiry.lesson_plan_id)) {
        return (
          <EnquiryLessonPlan
            key={lesson_plan.id}
            lesson_plan={lesson_plan}
            enquiry_id={this.props.enquiry.id}
            tutoring_job={lesson_plan.tutoring_job}
            customer={this.props.enquiry.customer}
            subjects={this.props.subjects}
            tutor_results={this.props.tutor_results}
            tutor_proposals={this.props.tutor_proposals}
            learners={this.props.learners}
            pricing_calculator={this.props.pricing_calculator}
            google_map_ui={this.props.ui.google_map}
            ui={this.props.ui}
            change_applied={this.props.change_applied}
          />
        );
      }
    }

    return null;
  };

  onCommissionManagerChange = (commission_manager_id) => {
    store.emit(
      "enquiry:update_commission_manager",
      this.props.enquiry.id,
      commission_manager_id,
    );
  };

  render() {
    const enquiry = this.props.enquiry;
    const renderLessonPlan = this.renderLessonPlan;
    const commission_manager = enquiry.commission_manager;

    return (
      <div>
        {enquiry.deactivated ? (
          <div className="min-h-[49px] bg-destructive p-2 text-white" role="alert">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {enquiry.deactivation_reason ? (
                <h4 className="mb-0 font-bold uppercase">
                  Deactivated {enquiry.deactivation_reason.reason}{" "}
                  <Timeago date={enquiry.deactivation_reason.time} />
                </h4>
              ) : (
                <h4 className="mb-0 font-bold uppercase">Deactivated</h4>
              )}
              {enquiry.actions.can_reactivate &&
              !this.props.ui.enquiry.buttons.support_handover.open ? (
                <ReactivateButton
                  enquiry_id={enquiry.id}
                  ui={this.props.ui.enquiry.buttons.reactivate}
                />
              ) : null}
            </div>
          </div>
        ) : null}
        {enquiry.handed_over ? (
          <div className="bg-[#3ecf8e] p-2 text-white" role="alert">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-1 items-center gap-4">
                <h4 className="mb-0 font-bold uppercase">Handed Over</h4>
                {enquiry.handed_over_url ? (
                  <Button asChild variant="secondary" size="sm">
                    <a href={enquiry.handed_over_url} target="_blank">
                      View <ExternalLink className="size-3.5" />
                    </a>
                  </Button>
                ) : null}
              </div>
              {commission_manager ? (
                <div className="w-1/3">
                  {enquiry.actions.can_change_commission_manager ? (
                    <EditCommissionManager
                      claim_managers={enquiry.claim_managers}
                      commission_manager_id={commission_manager.id}
                      onChange={this.onCommissionManagerChange}
                    />
                  ) : (
                    <div className="text-right font-semibold">
                      Sales Commission: {commission_manager.name}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {enquiry.actions.can_claim ? (
          <ClaimButton
            enquiry_id={enquiry.id}
            sales_manager={enquiry.manager}
          />
        ) : null}
        <EnquiryHeader enquiry={enquiry} ui={this.props.ui} />
        <div>
          <div>
            {this.props.ui.enquiry.tab_key === "overview" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <EnquiryOverview
                  enquiry={enquiry}
                  learners={this.props.learners}
                  lesson_plans={this.props.lesson_plans}
                  orders={this.props.orders}
                />
              </div>
            ) : null}
            {this.props.ui.enquiry.tab_key === "activity" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                  <ActivityStream
                    comments={enquiry.comments}
                    comms={enquiry.comms}
                    commentable_type="Sales::Enquiry"
                    commentable_id={enquiry.id}
                  />
                  <Tasks
                    key={`tasks-Sales::Enquiry::${enquiry.id}::${enquiry.updated_at}`}
                    taskable_type="Sales::Enquiry"
                    taskable_id={enquiry.id}
                  />
                </div>
              </div>
            ) : null}
            {this.props.ui.enquiry.tab_key === "learners" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <EnquiryLearners
                  learners={this.props.learners}
                  enquiry_id={enquiry.id}
                  subjects={this.props.subjects}
                  learner_subjects={this.props.learner_subjects}
                  country_code={enquiry.country_code}
                  customer={enquiry.customer}
                />
                <div className="text-center">
                  <Button size="sm" onClick={this.onAddLearner}>
                    Add Learner
                  </Button>
                </div>
              </div>
            ) : null}
            {_.map(this.props.lesson_plans, function (lesson_plan) {
              return renderLessonPlan(lesson_plan);
            })}
            {this.props.ui.enquiry.tab_key === "orders" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <SalesOrder
                  enquiry_id={enquiry.id}
                  orders={this.props.orders}
                />
              </div>
            ) : null}
            {this.props.ui.enquiry.tab_key === "related_enquiries" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Link2 className="size-4" />
                      Related Enquiries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="divide-y">
                      {_.map(enquiry.related_resources, (resource) => {
                        const param = resource.type === "Lead" ? "l" : "e"
                        return (
                          <a
                            key={resource.id}
                            href={`?${param}=${resource.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block py-3 no-underline hover:bg-muted/50"
                          >
                            <DateComponent date={resource.created_at} />: {resource.type} {resource.id} {resource.status}{resource.manager ? ` by ${resource.manager.name}` : ''}
                          </a>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}
            {this.props.ui.enquiry.tab_key === "close" ? (
              <div className="flex flex-1 flex-col gap-4 bg-muted p-4">
                <CloseEnquiry enquiry={enquiry} ui={this.props.ui} />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

export default Enquiry;
